import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { Config } from '@config'
import { Icon, IconName, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { secureTokenStore, useCurrentUserStore } from '@store'
import { ScreenProps } from '@navigation'
import { translateApiError } from '@utils'

/**
 * LOCAL POC ONLY (DEMO_MODE=true): switch between the demo accounts in one
 * click instead of logging out and typing credentials. `/demo?as=orga&next=/events/new`
 * does it straight away (used by the links of the POC overview page).
 * The accounts are the fictional ones created by keeptouch-api
 * `scripts/poc/seed-demo-events.mjs`; they do not exist anywhere else.
 */
type PersonaId = 'visitor' | 'attendee' | 'orga' | 'admin'

interface Persona {
  id: PersonaId
  icon: IconName
  email?: string
  defaultNext: string
}

const DEMO_PASSWORD = 'Demo1234!'
const PERSONAS: Persona[] = [
  { id: 'visitor', icon: 'eye', defaultNext: '/e/AFTWORK1' },
  { id: 'attendee', icon: 'user', email: 'alice@demo.keeptouch.local', defaultNext: '/e/SUMMIT26' },
  { id: 'orga', icon: 'star', email: 'demo-orga@keeptouch.local', defaultNext: '/events' },
  { id: 'admin', icon: 'settings', email: 'demo-admin@keeptouch.local', defaultNext: '/admin' },
]

const SHORTCUTS: { labelKey: string; persona: PersonaId; next: string }[] = [
  { labelKey: 'demo.shortcuts.qr', persona: 'visitor', next: '/e/AFTWORK1' },
  { labelKey: 'demo.shortcuts.ended', persona: 'attendee', next: '/e/SUMMIT26' },
  { labelKey: 'demo.shortcuts.dashboard', persona: 'orga', next: '/events' },
  { labelKey: 'demo.shortcuts.create', persona: 'orga', next: '/events/new' },
  { labelKey: 'demo.shortcuts.admin', persona: 'admin', next: '/admin/requests' },
]

// Only same-app paths: never follow an absolute URL passed in the query.
const safeNext = (value: unknown, fallback: string) =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value : fallback

export const DemoSwitch: React.FC<ScreenProps<'DemoSwitch'>> = ({ route }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const queryClient = useQueryClient()
  const setUserID = useCurrentUserStore((state) => state.setUserID)
  const resetUserID = useCurrentUserStore((state) => state.resetUserID)
  const [busy, setBusy] = useState<PersonaId | null>(null)
  const [error, setError] = useState('')
  const autoStarted = useRef(false)
  const enabled = Config.DEMO_MODE === 'true'

  const switchTo = useCallback(
    async (personaId: PersonaId, next?: string) => {
      const persona = PERSONAS.find((p) => p.id === personaId)
      if (!persona) return
      setBusy(personaId)
      setError('')
      try {
        await secureTokenStore.clearTokens()
        resetUserID()
        queryClient.clear()
        if (persona.email) {
          const { data } = await api.post<{
            account: { id: string }
            accessToken: string
            refreshToken: string
          }>('/authentication/sign-in', { email: persona.email, password: DEMO_PASSWORD })
          await secureTokenStore.setTokens(data.accessToken, data.refreshToken)
          setUserID(data.account.id)
        }
        // Full reload: every screen starts clean for the new account.
        window.location.assign(safeNext(next, persona.defaultNext))
      } catch (err) {
        setBusy(null)
        setError(translateApiError(t, err).message)
      }
    },
    [queryClient, resetUserID, setUserID, t],
  )

  useEffect(() => {
    const as = route.params?.as
    if (!enabled || autoStarted.current || !as) return
    if (!PERSONAS.some((p) => p.id === as)) return
    autoStarted.current = true
    void switchTo(as as PersonaId, route.params?.next)
  }, [enabled, route.params, switchTo])

  if (!enabled) {
    return (
      <View style={styles.center}>
        <Text type="titleBold" center>
          {t('demo.disabled')}
        </Text>
      </View>
    )
  }

  if (route.params?.as && !error) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.colors.primary} />
        <Text type="body" center color={theme.colors.greyBlue}>
          {t('demo.switching')}
        </Text>
      </View>
    )
  }

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Text type="titleBigBold">{t('demo.title')}</Text>
        <Text type="body" color={theme.colors.greyBlue}>
          {t('demo.subtitle')}
        </Text>
      </View>

      <View style={styles.grid}>
        {PERSONAS.map((persona) => (
          <TouchableOpacity
            key={persona.id}
            accessibilityRole="button"
            testID={`demo-${persona.id}`}
            disabled={busy !== null}
            onPress={() => void switchTo(persona.id)}
            style={[styles.card, busy === persona.id && styles.cardBusy]}
          >
            <View style={styles.iconBubble}>
              <Icon name={persona.icon} size={22} color={theme.colors.primary} />
            </View>
            <Text type="subtitleBold">{t(`demo.personas.${persona.id}.name`)}</Text>
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t(`demo.personas.${persona.id}.description`)}
            </Text>
            {persona.email ? (
              <Text type="bodyExtraSmall" color={theme.colors.primary}>
                {persona.email}
              </Text>
            ) : null}
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.shortcuts}>
        <Text type="bodySmallBold" color={theme.colors.secondary}>
          {t('demo.shortcuts.title')}
        </Text>
        {SHORTCUTS.map((shortcut) => (
          <TouchableOpacity
            key={shortcut.labelKey}
            accessibilityRole="link"
            disabled={busy !== null}
            onPress={() => void switchTo(shortcut.persona, shortcut.next)}
            style={styles.shortcut}
          >
            <Text type="body" color={theme.colors.primary}>
              {t(shortcut.labelKey)}
            </Text>
            <Icon name="arrowRight" size={16} color={theme.colors.primary} />
          </TouchableOpacity>
        ))}
      </View>

      {error ? (
        <Text type="bodySmall" color={theme.colors.error}>
          {error}
        </Text>
      ) : null}
      <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
        {t('demo.note')}
      </Text>
    </ScrollView>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    page: { flex: 1, backgroundColor: theme.colors.pageBackground },
    content: {
      width: '100%',
      maxWidth: 960,
      alignSelf: 'center',
      padding: theme.spacing.xl,
      gap: theme.spacing.xl,
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.md,
      padding: theme.spacing.xxl,
    },
    header: { gap: theme.spacing.xs },
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.lg },
    card: {
      flexGrow: 1,
      flexBasis: 260,
      gap: theme.spacing.xs,
      padding: theme.spacing.xl,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
    },
    cardBusy: { opacity: 0.6 },
    iconBubble: {
      width: 44,
      height: 44,
      borderRadius: 22,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.sidebar,
      marginBottom: theme.spacing.xs,
    },
    shortcuts: {
      gap: theme.spacing.xs,
      padding: theme.spacing.xl,
      borderRadius: theme.border.jumbo,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    shortcut: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey,
    },
  })
